/** @format */
import React, { useState, useMemo } from 'react'
import styled from 'styled-components'
import { _$, Button, Select, Option, Editable, Message, Dialog } from '@king-design/react'
import { Divider, TablePage } from 'src/components'
import { nameValidate, targetMethod } from 'src/config'
import _ from 'lodash'
// eslint-disable-next-line max-len
import CustomAlarm, {
  itemProps
} from 'src/containers/MonitorCustommonitor/CustommonitorForm/MonitorCustomAlarmForm'
import { request } from 'src/utils'

interface ITabSelectedProps {
  className?: string
  region: string
  tableMetricList: any[]
  updateLocalMetricList: (value: any) => void
}

const TabSelected: React.FC<ITabSelectedProps> = ({
  className,
  region,
  tableMetricList,
  updateLocalMetricList
}) => {
  const [alarmVisible, setAlarmVisible] = useState<boolean>(false)
  const [alarmDefaultValue, setAlarmDefaultValue] = useState<itemProps>({})

  // 修改图例名称、统计方法、统计周期
  const changeMetricList = (uniqueId, key, value) => {
    const updateMetricList = _.cloneDeep(tableMetricList)
    updateMetricList.forEach(item => {
      if (item.uniqueId === uniqueId) {
        item[key] = value
      }
    })
    updateLocalMetricList(updateMetricList)
  }

  // 删除绘图（当前region下的）
  const deleteChart = (uniqueId?) => {
    if (uniqueId) {
      // 单独删除
      const updateMetricList = tableMetricList.filter(localItem => localItem.uniqueId !== uniqueId)
      updateLocalMetricList(updateMetricList)
    } else {
      // 全部清空
      const localMetricList = JSON.parse(localStorage.getItem('metricList'))
      const updateMetricList = localMetricList.filter(localItem => localItem.region !== region)
      updateLocalMetricList(updateMetricList)
    }
  }

  // 图例名称验证
  const _metricLegendValidate = (value, uniqueId) => {
    // 2-64个字符组成，支持中文，字母，数字，以及-_@#.
    if (!nameValidate.validate.test(value)) {
      Message.error(nameValidate.tips)
      return false
    }
    // 是否有重复的
    const localMetricList = JSON.parse(localStorage.getItem('metricList'))
    const repeatLegend = localMetricList.some(
      localItem => localItem.uniqueId !== uniqueId && localItem.metricLegend === value
    )
    if (repeatLegend) {
      Message.error(_$('图例名称重复，请重新命名'), 3000)
      return false
    }
    return true
  }

  // table scheme
  const scheme = useMemo(() => {
    return {
      alias: {
        title: _$('图例名称'),
        template: function (item) {
          return (
            <Editable
              value={item.alias}
              validate={value => _metricLegendValidate(value, item.uniqueId)}
              onChangeValue={value => {
                changeMetricList(item.uniqueId, 'alias', value)
              }}
            >
              <p className="metric-legend">{item.alias}</p>
            </Editable>
          )
        }
      },
      nameSpace: _$('命名空间'),
      metricName: _$('指标名称'),
      dimension: _$('维度'),
      method: {
        title: _$('统计方法'),
        template: function (item) {
          return (
            <Select
              value={item.method}
              width={100}
              onChangeValue={value => {
                changeMetricList(item.uniqueId, 'method', value)
              }}
            >
              {targetMethod.map(({ label, value }) => (
                <Option key={value} value={value}>
                  {_$(label)}
                </Option>
              ))}
            </Select>
          )
        }
      },
      period: {
        title: _$('统计周期'),
        template: function (item) {
          return <p>{item.period}分钟</p>
        }
      },
      active: {
        title: _$('操作'),
        template: item => {
          return (
            <div className="ksc-space">
              <a
                className="primary-color"
                onClick={() => {
                  openAlarm(item)
                }}
              >
                {_$('添加警报')}
              </a>
              <Divider />
              <a className="primary-color" onClick={() => deleteChart(item.uniqueId)}>
                {_$('删除绘图')}
              </a>
            </div>
          )
        }
      }
    }
  }, [tableMetricList])

  // 打开添加报警抽屉
  const openAlarm = item => {
    const { nameSpace, metricName, dimension, period, method, region } = item
    const defaultValue = {
      nameSpace,
      metricName,
      dimension,
      period,
      method,
      region
    }
    setAlarmDefaultValue(() => defaultValue)
    setAlarmVisible(true)
  }

  // 关闭添加报警抽屉
  const closeAlarm = () => {
    setAlarmVisible(false)
    setAlarmDefaultValue({})
  }

  // 提交添加报警
  const alarmSubmit = async params => {
    const userId = localStorage.getItem('userId')
    const {
      region,
      nameSpace,
      policyName,
      metricName,
      cycles,
      op,
      thresHold,
      period,
      method,
      dimension
    } = params
    const verifyData = await request('/cm/alarms/CheckPolicy', {
      method: 'post',
      data: {
        region,
        nameSpace,
        policyName,
        metricName,
        cycles,
        op,
        thresHold,
        period,
        method,
        userId,
        dimension
      }
    })
    if (verifyData?.code === 'Success') {
      createCustomRule(params)
    } else {
      const regex = /\{(.+?)\}/g
      const alartMessage = verifyData?.['message']
      if (alartMessage.match(regex)) {
        const showMessage = alartMessage.match(regex)[1]
        const repeatName = /\{(.+?)\}/.exec(showMessage)[1]
        Dialog.confirm({
          content: _$('指标已存在相同警报，确定要继续添加吗？', { repeatName })
        }).then(() => {
          createCustomRule(params)
        })
      } else {
        Message.error(verifyData?.['message'])
      }
    }
  }

  // 创建警报
  const createCustomRule = async params => {
    const result = await request('/cm/alarms/NewPolicy', {
      method: 'POST',
      data: { ...params }
    })
    setAlarmVisible(false)
    if (result?.code === 'Success') {
      Message.success(_$('添加成功'))
    }
    return true
  }

  return (
    <div className={className}>
      <div className="header">
        <Button type="primary" disabled={!tableMetricList?.length} onClick={() => deleteChart()}>
          {_$('全部清空')}
        </Button>
      </div>
      {scheme ? (
        <TablePage
          tableProps={{
            checkType: 'none',
            data: tableMetricList || [],
            scheme
          }}
        />
      ) : null}
      <CustomAlarm
        defaultValue={alarmDefaultValue}
        visible={alarmVisible}
        cancel={closeAlarm}
        region={alarmDefaultValue?.region}
        onOk={alarmSubmit}
      />
    </div>
  )
}

export default styled(TabSelected)`
  margin-top: 16px;
  .k-input {
    width: 100%;
  }
  .k-editable {
    .c-ellipsis {
      white-space: normal;
    }
  }
  .metric-legend {
    max-width: 100%;
    margin: 0;
    word-break: break-all;
  }
`
